import { Event } from "react-big-calendar";
import {
  areDatesEqual,
  convertToYYYYMMDD,
  getAllDatesBetween,
  getLocalISO,
} from "./date-utils";
import { v4 as uuidv4 } from "uuid";
import { ALL, GBP_SYMBOL, UNAVAILABLE_DAYS, USER_ROLES } from "lib/constants";
import { DateTime } from "luxon";

export const getUniqueId = (prefix: string = ""): string =>
  `${prefix}${uuidv4()}`;

const currencyFormatter = new Intl.NumberFormat("en-UK", {
  style: "currency",
  currency: "GBP",
});

export const formatCurrency = (amount: number): string =>
  currencyFormatter.format(amount);

export const formatRoomNumber = (roomNumber: number): string =>
  `Room-${(roomNumber ?? 0).toString().padStart(2, "0")}`;

export const handleCopy = (walletAddress: string, message: string) => {
  navigator.clipboard.writeText(walletAddress);
  alert(message);
};

export const toTitleCase = (str: string): string =>
  str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );

export const getDateClassname = (
  calendarDetails: CalendarDetails,
  unavailableDays: UnavailableDay[],
  date: Date
): string => {
  const currentDate = convertToYYYYMMDD(date);

  if (!calendarDetails) return "none";

  return (
    (unavailableDays ?? []).filter(
      (holiday) => holiday.date === currentDate
    )?.[0]?.type ?? "none"
  );
};

export const getDayTooltip = (unavailableDays: UnavailableDay[], date: Date) =>
  (unavailableDays ?? []).filter((holiday) =>
    areDatesEqual(DateTime.fromISO(holiday.date).toJSDate(), date)
  )?.[0]?.title;

export const isAdminUser = (role: string): boolean => role === USER_ROLES.ADMIN;
export const isSuperAdminUser = (role: string): boolean =>
  role === USER_ROLES.SUPER;
export const isPatientUser = (role: string): boolean =>
  role === USER_ROLES.PATIENT;
export const isUnknownUser = (role: string): boolean => role === "unknown";
export const isPractitionerUser = (role: string): boolean =>
  role === USER_ROLES.PRACTITIONER;
export const isShowProfile = (role: string): boolean =>
  isPractitionerUser(role);

export const getCalendarEventsFor = (
  days: UnavailableDay[],
  type: string,
  titleOverride?: string
): Event[] =>
  (days ?? []).map((day) => {
    const title =
      titleOverride ??
      (type === UNAVAILABLE_DAYS.HOLIDAY
        ? day.title
        : type === UNAVAILABLE_DAYS.PRACTITIONER_BOOKINGS
        ? "Practitioner Booking"
        : "Unavailable");

    const date = DateTime.fromJSDate(day.internalDate, { zone: "utc" }).startOf("day").toJSDate();
    return {
      start: date,
      end: date,
      title,
      allDay: true,
      resource: {
        type,
        title,
      },
    };
  });

export const getBillableDays = (
  startDate: Date,
  endDate: Date,
  nonBillableDates: Date[],
  selectedWeekdays: string[]
) => {
  const allDates = getAllDatesBetween(startDate, endDate, selectedWeekdays);
  const nonBillableSet = new Set(
    (nonBillableDates ?? []).map((date) => getLocalISO(date))
  );
  return allDates.filter((date) => !nonBillableSet.has(getLocalISO(date)));
};

export const getFullName = (firstName: string, lastName: string): string =>
  (firstName ?? "") + " " + (lastName ?? "");

export const isNullOrAll = (value?: string): boolean =>
  value?.localeCompare(ALL) === 0 || value === null || value === undefined;

export const openUrlInNewTab = (url: string) => {
  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
};

export const callbackWithDelay = (
  delayInMilliseconds: number,
  callbackFn: () => void
) => window.setTimeout(callbackFn, delayInMilliseconds);

export const openUrlInSameTab = (url: string) => window.open(url, "_self");

export const getGreetingMessage = (): string => {
  let greet = "Good Morning";

  const hours = new Date().getHours();

  if (hours >= 12 && hours <= 17) greet = "Good Afternoon";
  else if (hours > 17 && hours <= 24) greet = "Good Evening";

  return greet;
};
